import type { User } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';

type LogLevel = 'info' | 'warn' | 'error';

const LOG_COLORS = {
  info: '#00b34a',     // VS Code green
  warn: '#ffcc00',     // VS Code yellow
  error: '#ff3c3c'     // VS Code red
};

interface LogContext {
  user?: User | null;
  component?: string;
  action?: string;
  [key: string]: unknown;
}

class Logger {
  private static formatMessage(level: LogLevel, message: string, context?: LogContext): [string, string[]] {
    const timestamp = new Date().toISOString();
    const domReplacer = (_key: string, value: unknown) => {
      if (value instanceof Element) {
        return `[${value.tagName.toLowerCase()}${value.id ? '#' + value.id : ''}]`;
      }
      return value;
    };

    const contextStr = context ? Object.entries(context)
      .map(([k, v]) => {
        const val = typeof v === 'object' ? 
          (v ? JSON.stringify(v, domReplacer) : 'null') : 
          String(v);
        return `${k}:${val}`;
      })
      .join(',') : '';

    // Browser-friendly CSS formatting with visual hierarchy
    const styles = [
      'background: #2d2d2d; color: #00ffff; padding: 2px 4px; border-radius: 3px 0 0 3px; font-size: 0.85em;', // timestamp (neon cyan on dark bg)
      `background: ${LOG_COLORS[level]}; color: black; padding: 2px 5px; border-radius: 0 3px 3px 0; font-weight: bold; text-transform: uppercase; font-size: 0.9em;`, // level (black text on color)
      'color: #39FF14; font-size: 1.1em; padding: 0 8px;', // message (neon green)
      'color: #ff00ff; font-weight: bold;', // separator
      'color: #FFA500; background: #2d2d2d; padding: 2px 5px; border-radius: 3px; font-size: 0.9em;' // context (neon orange on dark bg)
    ];
    
    // Format with better separation between elements
    return [
      `%c${timestamp}%c${level.toUpperCase()}%c ${message}%c${contextStr ? ' |' : ''}%c${contextStr || ''}`,
      styles
    ];
  }

  static info(message: string, context?: LogContext) {
    const [fmt, styles] = this.formatMessage('info', message, context);
    console.info(`%c${fmt}`, ...styles);
    
    // Send to Sentry
    try {
      const { logger } = Sentry;
      logger.info(message, context);
    } catch (e) {
      // Silently fail if Sentry is not available
    }
  }

  static warn(message: string, context?: LogContext) {
    const [fmt, styles] = this.formatMessage('warn', message, context);
    console.warn(`%c${fmt}`, ...styles);
    
    // Send to Sentry
    try {
      const { logger } = Sentry;
      logger.warn(message, context);
    } catch (e) {
      // Silently fail if Sentry is not available
    }
  }

  static error(message: string, error?: Error, context?: LogContext) {
    const [fmt, styles] = this.formatMessage('error', message, {
      ...context,
      error: error?.message,
      stack: error?.stack
    });
    console.error(
      `%c${fmt}`,
      ...styles,
      error?.stack ? '\nStack: ' + error.stack : ''
    );
    
    // Send to Sentry
    try {
      const { logger } = Sentry;
      logger.error(message, {
        ...context,
        error: error?.message,
        stack: error?.stack
      });
    } catch (e) {
      // Silently fail if Sentry is not available
    }
  }
}

export default Logger;
